import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { getIn } from 'formik';
import get from 'lodash-es/get';
import { useDebouncedCallback } from 'use-debounce';
import { InputGroup, InputFeedback, AsyncSelect } from '@components/common';
import API from '@utils/api';
import { ERRORS as ALL_ERRORS, getErrors } from '@utils/error';

import {
  autocompletionRequestBuilder,
  getSessionInstances
} from '@services/googleMap';

const defaultAutoCompletionRequest = {
  componentRestrictions: {
    country: ['us']
  },
  types: ['address']
};

function GooglePropertyField(props) {
  const { hideError, field, label, form, mb, ...rest } = props;

  const { placesService } = getSessionInstances();
  const { callback: fetchSuggestions } = useDebouncedCallback((value, cb) => {
    if (!placesService) {
      return cb([]);
    }

    if (value.length < 2) {
      return cb([]);
    }

    placesService.getPlacePredictions(
      autocompletionRequestBuilder(defaultAutoCompletionRequest, value),
      suggestions => {
        cb(
          (suggestions || []).map(suggestion => ({
            label: suggestion.description,
            value: suggestion
          }))
        );
      }
    );
  }, 300);

  const fetchPropertyInfo = useCallback(
    async googlePlaceId => {
      form.setSubmitting(true);
      try {
        const { result: propertyInfo } = await API.getPropertyFromGoogleId(
          googlePlaceId
        );
        form.setFieldValue('property', propertyInfo);
      } catch (err) {
        const error = getErrors(err);
        const { name } = error || {};

        if (name === ALL_ERRORS.OUT_OF_SERVICE_COVERAGE) {
          form.setFieldError(
            field.name,
            'We apologize, but currently there is no availability at this zip-code. We are constantly expanding however, and we will reach out once we start servicing your area.'
          );
        } else {
          form.setFieldValue('property', null);
        }
      }
      form.setSubmitting(false);
    },
    [form, field]
  );

  const handleOnChange = async val => {
    const googlePlaceId = get(val, 'value.place_id');
    form.setFieldValue(field.name, googlePlaceId);
    form.setFieldValue('buildingName', undefined);
    form.setFieldValue('property', null);

    if (googlePlaceId) {
      await fetchPropertyInfo(googlePlaceId);
    }
  };

  const formError = getIn(form.errors, field.name);
  const touched = getIn(form.touched, field.name);

  const hasError = !!(touched && formError);

  return (
    <InputGroup mb={mb}>
      <AsyncSelect
        disabled={form.isSubmitting}
        {...rest}
        placeholder={label}
        isClearable
        onChange={handleOnChange}
        loadOptions={fetchSuggestions}
      />
      {hasError && !hideError && (
        <InputFeedback mt={mb} mb={0}>
          {formError}
        </InputFeedback>
      )}
    </InputGroup>
  );
}

GooglePropertyField.propTypes = {
  field: PropTypes.object,
  form: PropTypes.object,
  label: PropTypes.string,
  mb: PropTypes.any,
  hideError: PropTypes.bool
};

export default GooglePropertyField;
